<script setup>
import { ref, computed } from 'vue'
import { router } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import { useToast } from '@/composables/useToast'

const props = defineProps({
  client:                  { type: Object,  default: null },
  logos:                   { type: Array,   default: () => [] },
  active_header_signed_id: { type: String,  default: '' },
  active_form_signed_id:   { type: String,  default: '' },
  back_url:                { type: String,  default: '/administration' },
  upload_url:              { type: String,  default: '' },
  set_active_url:          { type: String,  default: '' },
  delete_url:              { type: String,  default: '' },
})

const { success: toastSuccess, error: toastError } = useToast()

// ── Upload ────────────────────────────────────────────────────
const newFiles         = ref([])
const newPreviews      = ref([])
const newLabels        = ref([])
const uploading        = ref(false)
const fileInputRef     = ref(null)

function onFileChange(e) {
  Array.from(e.target.files || []).forEach(file => {
    newFiles.value.push(file)
    newLabels.value.push(file.name.replace(/\.[^.]+$/, ''))
    const reader = new FileReader()
    reader.onload = ev => newPreviews.value.push({ name: file.name, src: ev.target.result })
    reader.readAsDataURL(file)
  })
  e.target.value = ''
}

function removeNewFile(i) {
  newFiles.value.splice(i, 1)
  newPreviews.value.splice(i, 1)
  newLabels.value.splice(i, 1)
}

function uploadAll() {
  if (!newFiles.value.length) return
  uploading.value = true
  // Envoyer chaque fichier en FormData multipart
  const promises = newFiles.value.map((file, i) => {
    const fd = new FormData()
    fd.append('logo', file)
    fd.append('label', newLabels.value[i] || file.name)
    fd.append('_method', 'POST')
    return fetch(props.upload_url, {
      method: 'POST',
      headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content },
      body: fd,
      redirect: 'manual',
    })
  })
  Promise.allSettled(promises).then(() => {
    uploading.value = false
    newFiles.value = []
    newPreviews.value = []
    newLabels.value = []
    router.reload({ only: ['logos', 'active_header_signed_id', 'active_form_signed_id'] })
    toastSuccess('Logo(s) téléversé(s) avec succès.')
  }).catch(() => {
    uploading.value = false
    toastError('Erreur lors du téléversement.')
  })
}

// ── Logos existants ───────────────────────────────────────────
const logos = ref(props.logos.map(l => ({ ...l })))

const activeHeaderId = ref(props.active_header_signed_id)
const activeFormId   = ref(props.active_form_signed_id)

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

async function setActive(signed_id, role) {
  const res = await fetch(props.set_active_url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    body: JSON.stringify({ signed_id, role }),
  })
  if (res.ok) {
    if (role === 'header') activeHeaderId.value = signed_id
    else                   activeFormId.value   = signed_id
    logos.value.forEach(l => {
      if (role === 'header') l.is_header = l.signed_id === signed_id
      else                   l.is_form   = l.signed_id === signed_id
    })
    toastSuccess(`Logo ${role === 'header' ? 'entête' : 'formulaire'} mis à jour.`)
  } else {
    toastError('Erreur lors de la mise à jour.')
  }
}

const deletingId = ref(null)
async function deleteLogo(signed_id) {
  if (!confirm('Supprimer ce logo ?')) return
  deletingId.value = signed_id
  const res = await fetch(props.delete_url + '?signed_id=' + encodeURIComponent(signed_id), {
    method: 'DELETE',
    headers: { 'X-CSRF-Token': csrfToken() },
  })
  deletingId.value = null
  if (res.ok) {
    logos.value = logos.value.filter(l => l.signed_id !== signed_id)
    if (activeHeaderId.value === signed_id) activeHeaderId.value = ''
    if (activeFormId.value   === signed_id) activeFormId.value   = ''
    toastSuccess('Logo supprimé.')
  } else {
    toastError('Erreur lors de la suppression.')
  }
}

// nav active
const navItems = [
  { label: 'Liste',        href: '/administration' },
  { label: 'Client',       href: '/administration/edit_config_client' },
  { label: 'Logos',        href: '/administration/edit_logos',          active: true },
  { label: 'Bordereau',    href: '/administration/edit_config_bordereau' },
  { label: 'Importation',  href: '/administration/edit_config_importation' },
  { label: 'Modules',      href: '/administration/edit_config_module' },
]
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="text-xs font-black uppercase tracking-widest text-outline">
        Configuration &rsaquo; Logos
      </div>
    </template>

    <div class="p-8">
      <div class="max-w-6xl mx-auto">

        <!-- Header -->
        <div class="mb-8 flex justify-between items-end">
          <div>
            <h1 class="text-3xl font-black text-on-surface tracking-tight uppercase">Logos du client</h1>
            <p class="text-outline text-sm font-black uppercase tracking-widest mt-1">{{ client?.nom || '—' }}</p>
          </div>
          <a :href="back_url"
             class="flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all">
            <span class="material-symbols-outlined text-base">arrow_back</span>Retour
          </a>
        </div>

        <!-- Navigation -->
        <nav class="flex items-center gap-1 bg-surface-container-low rounded-md p-1 border border-outline-variant/10 mb-6">
          <a v-for="item in navItems" :key="item.href" :href="item.href"
             :class="['flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest rounded-sm transition-all',
               item.active
                 ? 'text-primary bg-surface-container border-l-4 border-tertiary'
                 : 'text-outline hover:text-on-surface hover:bg-surface-container/50']">
            {{ item.label }}
          </a>
        </nav>

        <!-- Empty state si pas de client -->
        <div v-if="!client" class="bg-surface-container rounded-xl p-12 border-l-4 border-primary text-center">
          <span class="material-symbols-outlined text-5xl text-outline/40 mb-3">domain_disabled</span>
          <p class="text-sm text-on-surface font-bold">Aucun client associé à ce sous-domaine.</p>
        </div>

        <template v-else>

          <!-- ── Logos existants ── -->
          <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden mb-6">
            <div class="px-6 py-4 bg-surface-container-high/60 border-b border-outline-variant/15 flex items-center justify-between">
              <h2 class="text-[11px] font-black uppercase tracking-widest text-on-surface">Logos disponibles</h2>
              <div class="flex items-center gap-3 text-[10px] text-outline font-black uppercase tracking-widest">
                <span class="flex items-center gap-1"><span class="w-3 h-3 rounded-full bg-primary inline-block"></span>Entête</span>
                <span class="flex items-center gap-1"><span class="w-3 h-3 rounded-full bg-tertiary inline-block"></span>Formulaire</span>
              </div>
            </div>

            <div v-if="!logos.length" class="p-8 text-center text-sm text-outline italic">
              Aucun logo téléversé. Ajoutez-en un ci-dessous.
            </div>

            <div v-else class="p-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              <div v-for="logo in logos" :key="logo.signed_id"
                   :class="['relative group flex flex-col rounded-xl border-2 overflow-hidden transition-all',
                     logo.is_header && logo.is_form ? 'border-primary shadow-lg shadow-primary/20'
                     : logo.is_header               ? 'border-primary/60'
                     : logo.is_form                 ? 'border-tertiary/60'
                     :                                'border-outline-variant/20']">

                <!-- Badges actifs -->
                <div class="absolute top-2 left-2 flex flex-col gap-1 z-10">
                  <span v-if="logo.is_header"
                        class="px-2 py-0.5 rounded-full text-[9px] font-black uppercase bg-primary text-on-primary shadow">
                    Entête
                  </span>
                  <span v-if="logo.is_form"
                        class="px-2 py-0.5 rounded-full text-[9px] font-black uppercase bg-tertiary text-on-tertiary shadow">
                    Formulaire
                  </span>
                </div>

                <!-- Bouton supprimer -->
                <button type="button" @click="deleteLogo(logo.signed_id)"
                        :disabled="deletingId === logo.signed_id"
                        class="absolute top-2 right-2 z-10 w-6 h-6 rounded-full bg-error/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow">
                  <span class="material-symbols-outlined text-sm">close</span>
                </button>

                <!-- Aperçu -->
                <a :href="logo.url" target="_blank" rel="noopener"
                   class="block bg-surface-container-highest/50 flex items-center justify-center min-h-[100px]">
                  <img :src="logo.thumbnail_url || logo.url"
                       :alt="logo.label"
                       class="max-h-[100px] w-full object-contain p-2 hover:opacity-80 transition-opacity" />
                </a>

                <!-- Info + actions -->
                <div class="p-3 bg-surface-container flex-1 flex flex-col gap-2">
                  <p class="text-[10px] font-bold text-on-surface truncate" :title="logo.label">{{ logo.label }}</p>
                  <p class="text-[9px] text-outline">{{ logo.created_at }}</p>

                  <div class="flex gap-1.5 mt-auto">
                    <!-- Définir comme entête -->
                    <button type="button" @click="setActive(logo.signed_id, 'header')"
                            :class="['flex-1 py-1 rounded text-[9px] font-black uppercase tracking-widest transition-all',
                              logo.is_header
                                ? 'bg-primary text-on-primary cursor-default'
                                : 'bg-primary/10 text-primary hover:bg-primary/20']"
                            :disabled="logo.is_header">
                      Entête
                    </button>
                    <!-- Définir comme formulaire -->
                    <button type="button" @click="setActive(logo.signed_id, 'form')"
                            :class="['flex-1 py-1 rounded text-[9px] font-black uppercase tracking-widest transition-all',
                              logo.is_form
                                ? 'bg-tertiary text-on-tertiary cursor-default'
                                : 'bg-tertiary/10 text-tertiary hover:bg-tertiary/20']"
                            :disabled="logo.is_form">
                      Formulaire
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ── Ajouter de nouveaux logos ── -->
          <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div class="px-6 py-4 bg-surface-container-high/60 border-b border-outline-variant/15">
              <h2 class="text-[11px] font-black uppercase tracking-widest text-on-surface">Ajouter des logos</h2>
            </div>
            <div class="p-6 space-y-4">

              <!-- Zone de dépôt -->
              <label class="flex items-center gap-3 px-5 py-4 border-2 border-dashed border-outline-variant/30 rounded-xl cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-all group">
                <span class="material-symbols-outlined text-3xl text-outline group-hover:text-primary transition-colors">add_photo_alternate</span>
                <span class="text-sm text-outline group-hover:text-on-surface transition-colors">Parcourir ou glisser des images (PNG, JPG, SVG)…</span>
                <input ref="fileInputRef" type="file" multiple accept="image/*" class="hidden" @change="onFileChange" />
              </label>

              <!-- Aperçus des nouveaux logos -->
              <div v-if="newPreviews.length" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                <div v-for="(img, i) in newPreviews" :key="i"
                     class="relative group rounded-xl border border-outline-variant/20 overflow-hidden">
                  <img :src="img.src" :alt="img.name" class="w-full h-28 object-contain bg-surface-container-highest/40 p-2" />
                  <div class="absolute top-1 left-1 bg-tertiary/80 text-white text-[9px] font-black uppercase px-1.5 py-0.5 rounded">Nouveau</div>
                  <button type="button" @click="removeNewFile(i)"
                          class="absolute top-1 right-1 w-5 h-5 rounded-full bg-error/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span class="material-symbols-outlined text-xs">close</span>
                  </button>
                  <div class="p-2">
                    <input v-model="newLabels[i]" type="text" placeholder="Nom du logo…"
                           class="w-full bg-surface-container-highest border border-outline-variant/20 rounded px-2 py-1 text-[10px] text-on-surface focus:outline-none focus:ring-1 focus:ring-primary/30" />
                  </div>
                </div>
              </div>

              <!-- Bouton upload -->
              <div v-if="newPreviews.length" class="flex justify-end">
                <button type="button" @click="uploadAll" :disabled="uploading"
                        class="machined-sheen px-6 py-3 rounded-md text-on-primary font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:brightness-110 transition-all shadow-lg disabled:opacity-60">
                  <span v-if="uploading" class="material-symbols-outlined text-sm animate-spin">progress_activity</span>
                  <span v-else class="material-symbols-outlined text-sm">cloud_upload</span>
                  Téléverser {{ newFiles.length > 1 ? `(${newFiles.length} logos)` : 'le logo' }}
                </button>
              </div>

            </div>
          </div>

          <!-- Info -->
          <div class="mt-4 px-4 py-3 bg-surface-container rounded-lg border border-outline-variant/10 flex items-start gap-3">
            <span class="material-symbols-outlined text-base text-outline mt-0.5">info</span>
            <p class="text-[11px] text-outline leading-relaxed">
              Cliquez sur <strong class="text-primary">Entête</strong> pour définir le logo affiché dans la barre de navigation.
              Cliquez sur <strong class="text-tertiary">Formulaire</strong> pour le logo utilisé dans les PDFs/formulaires.
              Un logo peut avoir les deux rôles simultanément.
            </p>
          </div>

        </template>

        <footer class="mt-12 pb-6 text-center text-outline text-[10px] uppercase tracking-widest font-black">
          Copyright &copy; 2016-{{ new Date().getFullYear() }} aurapport.com inc.
        </footer>
      </div>
    </div>
  </AppLayout>
</template>

